import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useListenContext } from '../contexts/ListenContext';
import { Mic, MicOff, Loader2, MessageSquare } from 'lucide-react';
import { cn } from '../lib/utils';

const ListenWidget = () => {
  const {
    isListening, isProcessing, conversationId, statusText, toggleListening
  } = useListenContext();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const showPanel = expanded || isListening || isProcessing;

  return (
    <>
      {/* Always-visible tab on the right edge */}
      <button
        onClick={() => {
          if (!showPanel) setExpanded(true);
          else if (!isListening && !isProcessing) setExpanded(false);
        }}
        onMouseEnter={() => setExpanded(true)}
        className={cn(
          "fixed right-0 top-1/2 -translate-y-1/2 z-[200] w-10 h-20 rounded-l-xl flex flex-col items-center justify-center gap-1 shadow-lg border border-r-0 transition-colors cursor-pointer",
          isListening
            ? "bg-red-500 border-red-400 text-white animate-pulse"
            : isProcessing
            ? "bg-amber-500 border-amber-400 text-white"
            : "bg-indigo-600 border-indigo-500 text-white hover:bg-indigo-700"
        )}
        data-testid="listen-widget-tab"
        title={isListening ? "Stop listening" : "Voice commands"}
      >
        {isProcessing ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <Mic className="w-5 h-5" />
        )}
      </button>

      {/* Slide-out panel */}
      <div
        className={cn(
          "fixed right-0 top-1/2 -translate-y-1/2 z-[199] transition-transform duration-300 ease-in-out",
          showPanel ? "translate-x-0" : "translate-x-full"
        )}
        onMouseLeave={() => {
          if (!isListening && !isProcessing) setExpanded(false);
        }}
        data-testid="listen-widget"
      >
        <div className="w-56 mr-10 bg-white rounded-l-xl shadow-2xl border border-r-0 border-slate-200 overflow-hidden">
          <div className="p-3 space-y-3">
            {/* Main listen button */}
            <button
              onClick={toggleListening}
              disabled={isProcessing}
              className={cn(
                "w-full h-12 rounded-lg flex items-center justify-center gap-2 font-medium text-sm orka-transition-colors",
                isListening
                  ? "bg-red-500 text-white hover:bg-red-600"
                  : isProcessing
                  ? "bg-amber-100 text-amber-700 cursor-wait"
                  : "bg-indigo-600 text-white hover:bg-indigo-700"
              )}
              data-testid="listen-button"
            >
              {isProcessing ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Processing</>
              ) : isListening ? (
                <><MicOff className="w-4 h-4" /> Stop</>
              ) : (
                <><Mic className="w-4 h-4" /> Listen</>
              )}
            </button>

            {/* Status */}
            {statusText && (
              <div className="text-xs text-slate-600 px-1 truncate" data-testid="listen-status">
                {statusText}
              </div>
            )}

            {/* Waveform when listening */}
            {isListening && (
              <div className="flex items-center justify-center gap-0.5 h-6">
                {[...Array(7)].map((_, i) => (
                  <div
                    key={i}
                    className="w-1 bg-red-400 rounded-full animate-pulse"
                    style={{
                      height: `${8 + Math.random() * 16}px`,
                      animationDelay: `${i * 100}ms`,
                      animationDuration: '0.6s'
                    }}
                  />
                ))}
              </div>
            )}

            {/* Go to chat */}
            {conversationId && (
              <button
                onClick={() => navigate(`/ai?conversation=${conversationId}`)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-indigo-600 hover:bg-indigo-50 transition-colors"
                data-testid="listen-chat-link"
              >
                <MessageSquare className="w-3.5 h-3.5" />
                View conversation
              </button>
            )}

            <p className="text-[10px] text-slate-400 px-1 leading-tight">
              {isListening ? "Speak your command. Stops after 10s silence." : "Click to start voice commands"}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default ListenWidget;
